import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack, Chip,
  LinearProgress, Paper, Avatar, Divider, IconButton, Tooltip,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import {
  TrendingUp, Person, CreditCard, CalendarMonth,
  Add as AddIcon, ArrowForward, CheckCircle, Schedule,
  AttachMoney, Assignment, Star, Phone, Business,
  Explore as ExploreIcon, Store as ChantierIcon,
  SupportAgent as SavIcon, Warning as AlertIcon,
  SwapHoriz as TransferIcon, Domain as DomainIcon,
  Description as CdcIcon, Dashboard as DashboardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import blobticService from '../../Commun/services/blobticService';
import { crmApi } from '../services/crmApi';
import { CrmDashboardStats, CrmLead, CrmOpportunity, CrmActivity } from '../types/crm';

export const DashboardPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [dashboardRole, setDashboardRole] = useState<'prescripteur' | 'kam' | 'global'>('prescripteur');
  const [stats, setStats] = useState<CrmDashboardStats>({
    totalLeads: 0,
    newLeads: 0,
    totalContacts: 0,
    totalOpportunities: 0,
    pipelineAmount: 0,
    wonAmount: 0,
    pendingActivities: 0,
    prescripteurVisitesRealisees: 0,
    prescripteurVisitesPlanifiees: 0,
    prescripteurCdcRealises: 0,
    prescripteurCdcEnCours: 0,
    prescripteurProjetsGagnesMois: 0,
    prescripteurProjetsGagnesAnnee: 0,
    prescripteurTauxVisiteCdc: 0,
    prescripteurTauxCdcGagne: 0,
    prescripteurNouveauxContacts: 0,
    prescripteurReseauActif: 0,
    prescripteurVisitesRetard: 0,
    prescripteurContactsRelancer: 0,
    prescripteurTransfertsAttente: 0,
    kamChantiersActifs: 0,
    kamChantiersDemarrage: 0,
    kamChantiersTermines: 0,
    kamCommandesTraitees: 0,
    kamCommandesEnCours: 0,
    kamCommandesRetard: 0,
    kamCaRealise: 0,
    kamMargeMoyenne: 0,
    kamEncaissementsPct: 0,
    kamSatisfactionPct: 0,
    kamReclamationsTotal: 0,
    kamReclamationsResolues: 0,
    kamVisitesChantier: 0,
    kamHeuresTerrain: 0,
    kamProblemesResolus: 0,
    kamProjetsRecus: 0
  });

  const [recentLeads, setRecentLeads] = useState<CrmLead[]>([]);
  const [opportunities, setOpportunities] = useState<CrmOpportunity[]>([]);
  const [recentActivities, setRecentActivities] = useState<CrmActivity[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const [s, leads, opps, acts] = await Promise.all([
        crmApi.getDashboardStats(),
        crmApi.getLeads(),
        crmApi.getOpportunities(),
        crmApi.getActivities(),
      ]);
      setStats(s);
      setRecentLeads(leads.slice(0, 5));
      setOpportunities(opps.slice(0, 5));
      setRecentActivities(acts.slice(0, 5));
    } catch (err) {
      console.error('Error loading CRM dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1600, margin: '0 auto' }}>
      {/* Header & Perspective Switcher */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { sm: 'center' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            {t('crm.dashboard.title', 'Tableau de Bord Commercial & CRM')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {dashboardRole === 'prescripteur'
              ? t('crm.dashboard.prescripteurSubtitle', 'Pilotage de la Prescription Amont, Détection BET & Suivi des CDC')
              : dashboardRole === 'kam'
              ? t('crm.dashboard.kamSubtitle', 'Pilotage Opérationnel Chantiers, Commandes & SAV')
              : t('crm.dashboard.globalSubtitle', 'Vue Consolidée de la Performance Commerciale')}
          </Typography>
        </Box>

        <ToggleButtonGroup
          value={dashboardRole}
          exclusive
          onChange={(_, val) => { if (val) setDashboardRole(val); }}
          size="small"
          sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
        >
          <ToggleButton value="prescripteur" sx={{ px: 2, fontWeight: 600 }}>
            <ExploreIcon sx={{ mr: 1, fontSize: 18 }} /> {t('crm.dashboard.viewPrescripteur', 'Vue Prescripteur')}
          </ToggleButton>
          <ToggleButton value="kam" sx={{ px: 2, fontWeight: 600 }}>
            <ChantierIcon sx={{ mr: 1, fontSize: 18 }} /> {t('crm.dashboard.viewKam', 'Vue KAM (Chantiers)')}
          </ToggleButton>
          <ToggleButton value="global" sx={{ px: 2, fontWeight: 600 }}>
            <DashboardIcon sx={{ mr: 1, fontSize: 18 }} /> {t('crm.dashboard.viewGlobal', 'Vue Globale')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading && <LinearProgress sx={{ mb: 3 }} />}

      {/* ========================================================================= */}
      {/* VUE 1 : PRESCRIPTEUR (Spécifications Section 7.2.9)                        */}
      {/* ========================================================================= */}
      {dashboardRole === 'prescripteur' && (
        <Stack spacing={3}>
          {/* Bloc 1: KPI Grille Principale */}
          <Grid container spacing={2.5}>
            {/* Visites */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: '#bae6fd',
                  bgcolor: '#eff6ff',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#0369a1">
                      {t('crm.dashboard.visitesTerrain', 'VISITES TERRAIN')}
                    </Typography>
                    <Avatar sx={{ bgcolor: '#e0f2fe', color: '#0284c7', width: 36, height: 36 }}>
                      <ExploreIcon fontSize="small" />
                    </Avatar>
                  </Stack>
                  <Typography variant="h4" fontWeight={800} color="#0284c7">
                    {stats.prescripteurVisitesRealisees || 0}
                    <Typography component="span" variant="h6" color="text.secondary"> / {(stats.prescripteurVisitesRealisees || 0) + (stats.prescripteurVisitesPlanifiees || 0)}</Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('crm.dashboard.visitesSub', 'Réalisées vs Planifiées ce mois')}
                  </Typography>
                </Box>
                <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: '#e0f2fe' }}>
                  <Typography variant="caption" fontWeight={600} color="#0369a1">
                    {t('crm.dashboard.tauxVisiteCdc', 'Taux de conversion Visite → CDC')} : {stats.prescripteurTauxVisiteCdc || 0}%
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* CDC Réalisés */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: '#c7d2fe',
                  bgcolor: '#eef2ff',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#4338ca">
                      {t('crm.dashboard.cahiersCharges', 'CAHIERS DES CHARGES')}
                    </Typography>
                    <Avatar sx={{ bgcolor: '#e0e7ff', color: '#4f46e5', width: 36, height: 36 }}>
                      <CdcIcon fontSize="small" />
                    </Avatar>
                  </Stack>
                  <Typography variant="h4" fontWeight={800} color="#4f46e5">
                    {stats.prescripteurCdcRealises || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('crm.dashboard.cdcSub', 'CDC signés / validés')} ({stats.prescripteurCdcEnCours || 0} {t('crm.dashboard.enCours', 'en cours')})
                  </Typography>
                </Box>
                <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: '#e0e7ff' }}>
                  <Typography variant="caption" fontWeight={600} color="#4338ca">
                    {t('crm.dashboard.tauxPreco', 'Taux d\'intégration préconisations')} : 85%
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Projets Gagnés */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: '#a7f3d0',
                  bgcolor: '#ecfdf5',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#047857">
                      {t('crm.dashboard.projetsGagnes', 'PROJETS GAGNÉS')}
                    </Typography>
                    <Avatar sx={{ bgcolor: '#d1fae5', color: '#059669', width: 36, height: 36 }}>
                      <CheckCircle fontSize="small" />
                    </Avatar>
                  </Stack>
                  <Typography variant="h4" fontWeight={800} color="#059669">
                    {stats.prescripteurProjetsGagnesAnnee || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('crm.dashboard.cumulAnnee', 'Cumul année')} ({stats.prescripteurProjetsGagnesMois || 0} {t('crm.dashboard.ceMois', 'ce mois-ci')})
                  </Typography>
                </Box>
                <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: '#d1fae5' }}>
                  <Typography variant="caption" fontWeight={600} color="#047857">
                    {t('crm.dashboard.tauxCdcGagne', 'Taux de succès CDC → Gagné')} : {stats.prescripteurTauxCdcGagne || 0}%
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Relations & Écosystème */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: '#ddd6fe',
                  bgcolor: '#f5f3ff',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#6d28d9">
                      {t('crm.dashboard.ecosystemePrescription', 'ÉCOSYSTÈME PRESCRIPTION')}
                    </Typography>
                    <Avatar sx={{ bgcolor: '#ede9fe', color: '#7c3aed', width: 36, height: 36 }}>
                      <DomainIcon fontSize="small" />
                    </Avatar>
                  </Stack>
                  <Typography variant="h4" fontWeight={800} color="#7c3aed">
                    {stats.prescripteurReseauActif || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('crm.dashboard.ecosystemeSub', 'Organisations actives (BET, Architectes, Promoteurs)')}
                  </Typography>
                </Box>
                <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: '#ede9fe' }}>
                  <Typography variant="caption" fontWeight={600} color="#6d28d9">
                    {t('crm.dashboard.satisfactionPartenaires', 'Satisfaction partenaires')} : 8.8 / 10 ⭐
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Bloc 2: Alertes & Objectifs Prescripteur */}
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
                  <AlertIcon /> {t('crm.dashboard.alertesPrioritaires', 'Alertes & Actions Prioritaires Prescripteur')}
                </Typography>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: '#fef2f2', borderRadius: 1.5 }}>
                    <Typography variant="body2" fontWeight={600} color="#b91c1c">
                      {t('crm.dashboard.visitesRetard', 'Visites terrain en retard')}
                    </Typography>
                    <Chip label={`${stats.prescripteurVisitesRetard || 0} ${t('crm.dashboard.enRetard', 'en retard')}`} size="small" color="error" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: '#fffbeb', borderRadius: 1.5 }}>
                    <Typography variant="body2" fontWeight={600} color="#b45309">
                      {t('crm.dashboard.contactsRelancer', 'Contacts clés BET à relancer')}
                    </Typography>
                    <Chip label={`${stats.prescripteurContactsRelancer || 2} ${t('crm.dashboard.aRelancer', 'à relancer')}`} size="small" color="warning" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: '#eff6ff', borderRadius: 1.5 }}>
                    <Typography variant="body2" fontWeight={600} color="#1d4ed8">
                      {t('crm.dashboard.transfertsAttente', 'Projets gagnés prêts pour transfert au KAM')}
                    </Typography>
                    <Chip label={`${stats.prescripteurTransfertsAttente || 0} ${t('crm.dashboard.enAttente', 'en attente')}`} size="small" color="primary" />
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ExploreIcon color="primary" /> {t('crm.dashboard.raccourcis', 'Raccourcis & Actions Rapides')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ExploreIcon />}
                    onClick={() => navigate('/CRM/visits')}
                    fullWidth
                  >
                    {t('crm.dashboard.planifierVisite', 'Planifier une Visite')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<CdcIcon />}
                    onClick={() => navigate('/CRM/cdc')}
                    fullWidth
                  >
                    {t('crm.dashboard.nouveauCdc', 'Nouveau CDC')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<TransferIcon />}
                    onClick={() => navigate('/CRM/opportunities')}
                    fullWidth
                  >
                    {t('crm.dashboard.projetsPipeline', 'Projets & Pipeline')}
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      )}

      {/* ========================================================================= */}
      {/* VUE 2 : KAM (Spécifications Section 7.3.4)                                 */}
      {/* ========================================================================= */}
      {dashboardRole === 'kam' && (
        <Stack spacing={3}>
          {/* Bloc 1: Métriques Opérationnelles & Financières */}
          <Grid container spacing={2.5}>
            {/* Chantiers */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: '#fed7aa',
                  bgcolor: '#fff7ed',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#c2410c">
                      {t('crm.dashboard.chantiersActifs', 'CHANTIERS ACTIFS')}
                    </Typography>
                    <Avatar sx={{ bgcolor: '#ffedd5', color: '#ea580c', width: 36, height: 36 }}>
                      <ChantierIcon fontSize="small" />
                    </Avatar>
                  </Stack>
                  <Typography variant="h4" fontWeight={800} color="#ea580c">
                    {stats.kamChantiersActifs || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stats.kamChantiersDemarrage || 0} {t('crm.dashboard.enDemarrage', 'en démarrage')} • {stats.kamChantiersTermines || 0} {t('crm.dashboard.termines', 'terminés')}
                  </Typography>
                </Box>
                <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: '#fed7aa' }}>
                  <Typography variant="caption" fontWeight={600} color="#c2410c">
                    {stats.kamProjetsRecus || 0} {t('crm.dashboard.projetsTransferes', 'projet(s) transféré(s) de la Prescription')}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Commandes Usine */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: '#bae6fd',
                  bgcolor: '#eff6ff',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#0369a1">
                      {t('crm.dashboard.commandesTraitees', 'COMMANDES TRAITÉES')}
                    </Typography>
                    <Avatar sx={{ bgcolor: '#e0f2fe', color: '#0284c7', width: 36, height: 36 }}>
                      <CreditCard fontSize="small" />
                    </Avatar>
                  </Stack>
                  <Typography variant="h4" fontWeight={800} color="#0284c7">
                    {stats.kamCommandesTraitees || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stats.kamCommandesEnCours || 0} {t('crm.dashboard.enCours', 'en cours')} • {stats.kamCommandesRetard || 0} {t('crm.dashboard.enRetard', 'en retard')}
                  </Typography>
                </Box>
                <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: '#e0f2fe' }}>
                  <Typography variant="caption" fontWeight={600} color={stats.kamCommandesRetard ? '#b91c1c' : '#0369a1'}>
                    {stats.kamCommandesRetard ? `⚠️ ${stats.kamCommandesRetard} ${t('crm.dashboard.commandesRetardCount', 'commande(s) en retard')}` : t('crm.dashboard.livraisonsConformes', 'Livraisons conformes')}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Financier CA DZD */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: '#a7f3d0',
                  bgcolor: '#ecfdf5',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#047857">
                      {t('crm.dashboard.chiffreAffaires', "CHIFFRE D'AFFAIRES")}
                    </Typography>
                    <Avatar sx={{ bgcolor: '#d1fae5', color: '#059669', width: 36, height: 36 }}>
                      <AttachMoney fontSize="small" />
                    </Avatar>
                  </Stack>
                  <Typography variant="h4" fontWeight={800} color="#059669">
                    {((stats.kamCaRealise || 0) / 1000000).toFixed(1)}M
                    <Typography component="span" variant="caption" color="text.secondary"> DZD</Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(stats.kamCaRealise || 0).toLocaleString()} DZD
                  </Typography>
                </Box>
                <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: '#d1fae5' }}>
                  <Typography variant="caption" fontWeight={600} color="#047857">
                    {t('crm.dashboard.margeMoy', 'Marge moy')} : {stats.kamMargeMoyenne || 0}% • {t('crm.dashboard.encaisse', 'Encaissé')} : {stats.kamEncaissementsPct || 0}%
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Qualité, SAV & Satisfaction */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: '#c7d2fe',
                  bgcolor: '#eef2ff',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#4338ca">
                      {t('crm.dashboard.satisfactionClient', 'SATISFACTION CLIENT')}
                    </Typography>
                    <Avatar sx={{ bgcolor: '#e0e7ff', color: '#4f46e5', width: 36, height: 36 }}>
                      <SavIcon fontSize="small" />
                    </Avatar>
                  </Stack>
                  <Typography variant="h4" fontWeight={800} color="#4f46e5">
                    {stats.kamSatisfactionPct || 85}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('crm.dashboard.satisfactionChantiersSub', 'Satisfaction globale sur les chantiers livrés')}
                  </Typography>
                </Box>
                <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: '#e0e7ff' }}>
                  <Typography variant="caption" fontWeight={600} color="#4338ca">
                    SAV : {stats.kamReclamationsResolues || 0} / {stats.kamReclamationsTotal || 0} {t('crm.dashboard.resolues', 'résolue(s)')}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Bloc 2: Activité Terrain & Raccourcis KAM */}
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ChantierIcon color="primary" /> {t('crm.dashboard.activiteTerrain', 'Activité Opérationnelle Terrain')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                      <Typography variant="h5" fontWeight={700} color="primary.main">
                        {stats.kamVisitesChantier || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{t('crm.dashboard.visitesChantier', 'Visites Chantier')}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                      <Typography variant="h5" fontWeight={700} color="secondary.main">
                        {stats.kamHeuresTerrain || 48}h
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{t('crm.dashboard.heuresTerrain', 'Heures Terrain')}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                      <Typography variant="h5" fontWeight={700} color="success.main">
                        {stats.kamProblemesResolus || 14}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{t('crm.dashboard.problemesResolus', 'Problèmes Résolus')}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SavIcon color="primary" /> {t('crm.dashboard.actionsKam', 'Actions Rapides KAM')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<ChantierIcon />}
                    onClick={() => navigate('/CRM/chantiers')}
                    fullWidth
                  >
                    {t('crm.dashboard.gererChantiers', 'Gérer les Chantiers')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<SavIcon />}
                    onClick={() => navigate('/CRM/reclamations')}
                    fullWidth
                  >
                    {t('crm.dashboard.reclamationsSav', 'Réclamations & SAV')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<ExploreIcon />}
                    onClick={() => navigate('/CRM/visits')}
                    fullWidth
                  >
                    {t('crm.dashboard.visitesTerrain', 'Visites Terrain')}
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      )}

      {/* ========================================================================= */}
      {/* VUE 3 : VUE GLOBALE & PIPELINE CONSOLIDÉ                                  */}
      {/* ========================================================================= */}
      {dashboardRole === 'global' && (
        <Stack spacing={3}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">{t('crm.dashboard.totalProspects', 'Total Prospects')}</Typography>
                <Typography variant="h4" fontWeight={700} color="primary.main">{stats.totalLeads}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">{t('crm.dashboard.contactsClients', 'Contacts Clients')}</Typography>
                <Typography variant="h4" fontWeight={700} color="secondary.main">{stats.totalContacts}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">{t('crm.dashboard.projetsPipeline', 'Projets en Pipeline')}</Typography>
                <Typography variant="h4" fontWeight={700} color="warning.main">{(stats.pipelineAmount || 0).toLocaleString()} DZD</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">{t('crm.dashboard.affairesGagnees', 'Affaires Gagnées')}</Typography>
                <Typography variant="h4" fontWeight={700} color="success.main">{(stats.wonAmount || 0).toLocaleString()} DZD</Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Listes récentes */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                  {t('crm.dashboard.projetsRecents', 'Projets & Opportunités Récents')}
                </Typography>
                <Stack spacing={1.5}>
                  {opportunities.map(opp => (
                    <Box key={opp.id || opp.COP_IDF} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>{opp.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{opp.code} • {opp.gamme || 'URBANIA'}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                          {(opp.amount || 0).toLocaleString()} DZD
                        </Typography>
                        <Chip label={opp.stage} size="small" />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                  {t('crm.dashboard.pistesRecentes', 'Pistes & Détections Récentes')}
                </Typography>
                <Stack spacing={1.5}>
                  {recentLeads.map(lead => (
                    <Box key={lead.id || lead.CLD_IDF} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>{lead.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{lead.company || lead.source}</Typography>
                      </Box>
                      <Chip label={lead.status} size="small" color={lead.status === 'NEW' ? 'info' : 'default'} />
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      )}
    </Box>
  );
};

export default DashboardPage;
